import PropTypes from "prop-types";
import { useEffect } from "react";
// @mui
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
// theme
import { navSurface } from "src/theme/palette";
// hooks
import { useResponsive } from "src/hooks/use-responsive";
// api
import { useGetVersion } from "src/api/system";
// auth
import { useAuthContext } from "src/auth/hooks";
// components
import Logo from "src/components/logo";
import Scrollbar from "src/components/scrollbar";
import { usePathname } from "src/routes/hook";
import { paths } from "src/routes/paths";
import { NavSectionVertical } from "src/components/nav-section";
// locales
import { useLocales } from "src/locales";
//
import { NAV } from "../config-layout";
import { useNavData } from "./config-navigation";
import { NavToggleButton } from "../_common";

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  const { user } = useAuthContext();

  const pathname = usePathname();

  const lgUp = useResponsive("up", "lg");

  const navData = useNavData();

  const { t } = useLocales();

  const { version } = useGetVersion();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
        // el scroll también vive sobre el negro de marca
        "& .simplebar-scrollbar:before": {
          bgcolor: theme => alpha(theme.palette.common.white, 0.24),
        },
      }}
    >
      <Logo
        sx={{ mt: 3, ml: 3, mb: 1, width: 184 }}
        onDark
        href={paths.dashboard.root}
      />

      <NavSectionVertical
        data={navData}
        config={{
          currentRole: user?.enterpriseRole || [],
          contrast: true,
        }}
      />

      <Box
        sx={{
          display: "flex",
          textAlign: "center",
          flexDirection: "column",
          py: 5,
          px: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: theme => alpha(theme.palette.common.white, 0.48) }}
        >
          {`${t("version")}: ${version}`}
        </Typography>
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: "fixed",
            width: NAV.W_VERTICAL,
            bgcolor: navSurface,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          sx={{
            // El override de MuiDrawer pinta el paper del drawer temporal desde el
            // root (`.MuiDrawer-root .MuiDrawer-paper`), así que le gana por
            // especificidad a cualquier sx puesto en PaperProps: el sidebar salía
            // con el color de página (claro en modo light) en vez del negro de
            // marca. Repitiendo el selector acá manda el sx, que se serializa
            // después de los styleOverrides del tema.
            [`& .${drawerClasses.paper}`]: {
              width: NAV.W_VERTICAL,
              bgcolor: navSurface,
              backgroundImage: "none",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};
